// src/views/ChatView.js — extracted from App.js
import React, { useState, useRef, useEffect } from "react";
import { supabase, upsertOne } from "../lib/supabase";
import { compressImage, timeAgo } from "../lib/utils";
import { SK } from "../constants";

// ─── CHAT VIEW ────────────────────────────────────────────────────────────────
export function ChatView({ session, sellers, users, requests, saveRequests, listings=[], openAuth, toast, catalogEntries, saveCatalog, watchModels=[], setView, addNotif, notifs=[], initialRoom=null, onRoomOpened }) {
  const [rooms, setRooms]           = useState([]);
  const [activeRoomId, setActiveRoomId] = useState("lounge");
  const [dealTab, setDealTab]       = useState("deals"); // sidebar sub-tab
  const [messages, setMessages]     = useState([]);
  const [draft, setDraft]           = useState("");
  const [loading, setLoading]       = useState(false);
  const [tab, setTab]               = useState("lounge");
  const [showChat, setShowChat]     = useState(false); // mobile: false=sidebar, true=chat pane
  const [uploading, setUploading]   = useState(false);
  const [catSearch, setCatSearch]   = useState("");
  const [showCatSearch, setShowCatSearch] = useState(false);
  const unreadKey = session?.user?.id ? `thc-unread-${session.user.id}` : null;
  const [unreadByRoom, setUnreadByRoom] = useState(() => {
    if (!unreadKey) return {};
    try { return JSON.parse(localStorage.getItem(unreadKey) || "{}"); } catch { return {}; }
  });
  const [dmQuery, setDmQuery]       = useState("");
  const [dealCtxOpen, setDealCtxOpen] = useState(false);
  const [showCounter, setShowCounter] = useState(false);
  const [counterInput, setCounterInput] = useState("");
  const [fetchedDealReq, setFetchedDealReq] = useState(null);
  const [botHistory, setBotHistory] = useState([]);
  const bottomRef = useRef();
  const photoInputRef = useRef();
  const isMobile = typeof window !== "undefined" && window.innerWidth < 768;
  // Rate-limit: track last N send timestamps to prevent spam
  const sendTimestamps = useRef([]);

  // Init: load lounge for guests too; only upsert (create) if logged in
  useEffect(() => {
    (async () => {
      if (session) {
        await supabase.from("chat_rooms").upsert(
          { id:"lounge", type:"lounge", name:"The Lounge", participants:[], created_at:new Date().toISOString() },
          { onConflict:"id", ignoreDuplicates:true }
        );
      }
      const { data } = await supabase.from("chat_rooms").select("*");
      setRooms(data || []);
      if (initialRoom) { setActiveRoomId(initialRoom); setShowChat(true); onRoomOpened?.(); }
    })();
  }, [session]); // eslint-disable-line

  // Load messages + realtime subscription when room changes
  useEffect(() => {
    if (!activeRoomId) return;
    setMessages([]); setLoading(true);
    // clear unread for this room when opening it
    setUnreadByRoom(prev => ({ ...prev, [activeRoomId]: 0 }));
    supabase.from("chat_messages").select("*").eq("room_id", activeRoomId)
      .order("created_at", { ascending: true })
      .then(({ data }) => { setMessages(data || []); setLoading(false); });

    const ch = supabase.channel("chat:" + activeRoomId)
      .on("postgres_changes", { event:"INSERT", schema:"public", table:"chat_messages", filter:`room_id=eq.${activeRoomId}` },
        p => setMessages(prev => [...prev, p.new])
      )
      .on("postgres_changes", { event:"DELETE", schema:"public", table:"chat_messages" },
        p => setMessages(prev => prev.filter(m => m.id !== p.old.id))
      ).subscribe();
    return () => { supabase.removeChannel(ch); };
  }, [activeRoomId]);

  // Fetch deal request from Supabase when not already in props (e.g. seller's stale requests array)
  useEffect(() => {
    const room = rooms.find(r => r.id === activeRoomId);
    if (!room?.request_id) { setFetchedDealReq(null); return; }
    const already = (requests||[]).find(r => r.id === room.request_id);
    if (already) { setFetchedDealReq(already); return; }
    supabase.from("requests").select("*").eq("id", room.request_id).single()
      .then(({ data }) => { if (data) setFetchedDealReq(data); else setFetchedDealReq(null); });
  }, [activeRoomId, rooms, requests]);

  // Global subscription for unread badges on non-active rooms
  useEffect(() => {
    if (!session) return;
    const ch = supabase.channel("chat:global-unread")
      .on("postgres_changes", { event:"INSERT", schema:"public", table:"chat_messages" },
        p => {
          const rid = p.new.room_id;
          if (rid !== activeRoomId && p.new.user_id !== session.user.id) {
            setUnreadByRoom(prev => ({ ...prev, [rid]: (prev[rid] || 0) + 1 }));
          }
        }
      ).subscribe();
    return () => { supabase.removeChannel(ch); };
  }, [session, activeRoomId]);

  // Persist unreadByRoom to localStorage whenever it changes
  useEffect(() => {
    if (!unreadKey) return;
    try { localStorage.setItem(unreadKey, JSON.stringify(unreadByRoom)); } catch {}
  }, [unreadByRoom, unreadKey]);

  useEffect(() => { bottomRef.current?.scrollIntoView({ behavior:"smooth" }); }, [messages]);

  // Tracks last DM-style notif fire-time per recipient+room to handle rapid-fire sends
  // before the parent's notifs prop has propagated back.
  const recentNotifRef = useRef({}); // { `${recipientId}|${roomId}`: epochMs }

  // Fire an in-app notification to every other participant when a message is sent
  // in a direct or deal room. Debounced 10 min per recipient per room so a long
  // back-and-forth doesn't flood the bell.
  const notifyOnSend = (room, sender, preview) => {
    if (!addNotif || !room || !room.type) return;
    if (room.type !== "direct" && room.type !== "deal") return;
    const recipients = (room.participants||[]).filter(id => id && id !== sender.id);
    if (recipients.length === 0) return;
    const WINDOW_MS = 10 * 60 * 1000;
    const now = Date.now();
    const isDM = room.type === "direct";
    const title = isDM ? `New message from ${sender.name}` : `New message in ${room.name || "deal room"}`;
    for (const rid of recipients) {
      const refKey = `${rid}|${room.id}`;
      const localTs = recentNotifRef.current[refKey] || 0;
      const propTs = (notifs||[])
        .filter(n => n.userId === rid && n.link?.roomId === room.id && (n.type === "dm" || n.type === "deal_msg"))
        .reduce((mx, n) => Math.max(mx, new Date(n.createdAt).getTime() || 0), 0);
      const lastTs = Math.max(localTs, propTs);
      if (now - lastTs < WINDOW_MS) continue;
      recentNotifRef.current[refKey] = now;
      addNotif(rid, {
        type: isDM ? "dm" : "deal_msg",
        title,
        body: preview,
        link: { view: "chat", roomId: room.id },
      });
    }
  };

  // Redact phone numbers (10-digit Indian mobiles, with or without spaces/dashes)
  const redactPhones = (text) => text.replace(/(?<!\d)(\+?91[\s-]?)?[6-9]\d{9}(?!\d)/g, "[number removed]");

  // ── Bot assistant ────────────────────────────────────────────────────────────
  const CHIP_ACTIONS = {
    "My orders": "my-orders",
    "Latest order status": "latest-order",
    "New sourcing requests": "new-sourcing",
    "My active listings": "my-listings",
    "How THC works": "how-it-works",
    "↩ Main menu": "main-menu",
  };

  const botMainChips = () => {
    const isSeller = !!(session?.user?.sellerId || (session?.user?.roles||[]).includes("seller"));
    return [
      "My orders",
      "Latest order status",
      ...(isSeller ? ["New sourcing requests", "My active listings"] : []),
      "How THC works",
    ];
  };

  const openBot = () => {
    setActiveRoomId("bot");
    setShowChat(true);
    if (!botHistory.length) {
      const name = session?.user?.name?.split(" ")[0] || "there";
      setBotHistory([{ role:"bot", text:`Hi ${name}! What can I help you with?`, chips: botMainChips() }]);
    }
  };

  const botRespond = (label) => {
    const action = CHIP_ACTIONS[label] || "main-menu";
    const uid = session?.user?.id;
    const sellerId = session?.user?.sellerId;
    const back = ["↩ Main menu"];

    setBotHistory(h => [...h, { role:"user", text: label }]);

    let text = "";
    let chips = back;

    switch (action) {
      case "my-orders": {
        const myReqs = (requests||[]).filter(r=>r.userId===uid).sort((a,b)=>new Date(b.createdAt)-new Date(a.createdAt));
        if (!myReqs.length) { text = "You haven't placed any orders yet."; chips = botMainChips(); break; }
        text = `Your ${myReqs.length} order${myReqs.length>1?"s":""} (newest first):\n\n` +
          myReqs.slice(0,6).map(r=>`• ${r.watch}\n  ${r.status} · ${r.id}`).join("\n\n") +
          (myReqs.length>6 ? `\n\n…and ${myReqs.length-6} more.` : "");
        break;
      }
      case "latest-order": {
        const myReqs = (requests||[]).filter(r=>r.userId===uid).sort((a,b)=>new Date(b.createdAt)-new Date(a.createdAt));
        if (!myReqs.length) { text = "No orders found."; chips = botMainChips(); break; }
        const r = myReqs[0];
        const lastUpdate = r.updates?.slice(-1)[0];
        text = `Latest order: ${r.watch}\nStatus: ${r.status}\nRef: ${r.id}` +
          (lastUpdate ? `\nLast update: ${lastUpdate.note}` : "") +
          (r.sellerClaim ? `\nSeller: ${r.sellerClaim.sellerName}` : "");
        break;
      }
      case "new-sourcing": {
        if (!sellerId) { text = "Only available for sellers."; chips = botMainChips(); break; }
        const OPEN = new Set(["Received","Awaiting Photos","Searching"]);
        const open = (requests||[]).filter(r=>(r.type==="sourcing"||r.type==="catalog")&&OPEN.has(r.status)&&!r.sellerClaim);
        if (!open.length) { text = "No open sourcing requests right now. Check your Sourcing tab later."; break; }
        text = `${open.length} open request${open.length>1?"s":""} you can claim:\n\n` +
          open.slice(0,6).map(r=>`• ${r.watch}${r.budget?" — budget ₹"+r.budget:""}`).join("\n") +
          "\n\nHead to your Sourcing tab to claim.";
        break;
      }
      case "my-listings": {
        if (!sellerId) { text = "Only available for sellers."; chips = botMainChips(); break; }
        const myL = (listings||[]).filter(l=>l.sellerId===sellerId);
        if (!myL.length) { text = "No listings yet. Go to your Seller Dash to add one."; break; }
        text = `Your ${myL.length} listing${myL.length>1?"s":""}\n\n` +
          myL.map(l=>`• ${l.brand} ${l.model} — ₹${l.price?.toLocaleString("en-IN")||"?"} (${l.verified?"Verified":"Pending"})`).join("\n");
        break;
      }
      case "how-it-works": {
        text = "THC connects vintage watch buyers with trusted sellers.\n\n• Find a Watch (₹99) — submit a request, we source it\n• Authenticate (₹300) — we verify and issue a certificate\n• Marketplace — browse listings from verified sellers\n\nIf we can't source your watch, your fee is credited back to your account.";
        chips = botMainChips();
        break;
      }
      default: {
        text = "What can I help you with?";
        chips = botMainChips();
      }
    }

    setTimeout(() => setBotHistory(h => [...h, { role:"bot", text, chips }]), 300);
  };

  const sendMessage = async () => {
    const raw = draft.trim();
    if (!raw || !session) return;
    if (!canSend) { toast("Submit a sourcing request first to unlock chat."); return; }
    if (rateLimited()) return;
    const text = redactPhones(raw);
    if (text !== raw) toast("Phone numbers are not allowed in chat — removed for your safety.");
    setDraft("");
    const { error } = await supabase.from("chat_messages").insert({
      id: "m" + Date.now() + Math.random().toString(36).slice(2,7),
      room_id: activeRoomId,
      user_id: session.user.id,
      user_name: session.user.name,
      text,
      created_at: new Date().toISOString()
    });
    if (error) { toast("Failed to send message — please retry."); setDraft(raw); return; }
    const room = rooms.find(r => r.id === activeRoomId);
    const preview = text.length > 80 ? text.slice(0, 80) + "…" : text;
    notifyOnSend(room, session.user, preview);
  };

  const startDirect = async (otherUser) => {
    const ids = [session.user.id, otherUser.id].sort();
    const roomId = "dm-" + ids.join("-");
    const { error } = await supabase.from("chat_rooms").upsert(
      { id:roomId, type:"direct", name:otherUser.name, participants:ids, created_at:new Date().toISOString() },
      { onConflict:"id", ignoreDuplicates:true }
    );
    if (error) { toast("Could not open direct message — please retry."); return; }
    const { data } = await supabase.from("chat_rooms").select("*");
    setRooms(data || []);
    setActiveRoomId(roomId);
    setTab("direct");
  };

  const clearMessages = async () => {
    await supabase.from("chat_messages").delete().eq("room_id", activeRoomId);
    setMessages([]);
    toast("Messages cleared.");
  };

  const deleteRoom = async () => {
    if (!window.confirm(`Delete room "${activeRoom?.name || activeRoomId}" and all its messages? This cannot be undone.`)) return;
    await supabase.from("chat_messages").delete().eq("room_id", activeRoomId);
    await supabase.from("chat_rooms").delete().eq("id", activeRoomId);
    setRooms(prev => prev.filter(r => r.id !== activeRoomId));
    setActiveRoomId("lounge");
    setMessages([]);
    toast("Room deleted.");
  };

  const sendPhoto = async (file) => {
    if (!file || !session) return;
    if (!canSend) { toast("Submit a sourcing request first to unlock chat."); return; }
    if (rateLimited()) return;
    setUploading(true);
    try {
      const path = `chat/${activeRoomId}/${Date.now()}.jpg`;
      const compressed = await compressImage(file, 1200, 0.82);
      const { error } = await supabase.storage.from("watch-photos").upload(path, compressed, { contentType:"image/jpeg", upsert:true });
      if (error) throw error;
      const { data: { publicUrl } } = supabase.storage.from("watch-photos").getPublicUrl(path);
      const { error: insertErr } = await supabase.from("chat_messages").insert({
        id: "m" + Date.now() + Math.random().toString(36).slice(2,7),
        room_id: activeRoomId,
        user_id: session.user.id,
        user_name: session.user.name,
        text: "",
        image_url: publicUrl,
        created_at: new Date().toISOString()
      });
      if (insertErr) throw insertErr;
      const room = rooms.find(r => r.id === activeRoomId);
      notifyOnSend(room, session.user, "📷 Sent a photo");
    } catch (e) { toast("Upload failed: " + e.message); }
    setUploading(false);
  };

  const isAdmin = session ? (session.user.role==="admin" || (session.user.roles||[]).includes("admin")) : false;
  const isSeller = isAdmin || (session ? (session.user.roles||[session.user.role]).includes("seller") : false);
  const isCollector = isAdmin || (session ? (session.user.roles||[session.user.role]).includes("collector") : false);
  // Chat send access: admin/seller/collector always; buyer only after submitting at least 1 request
  const myRequests = session ? (requests||[]).filter(r => r.userId === session.user.id) : [];
  const canSend = session && (isAdmin || isSeller || isCollector || myRequests.length > 0);

  const rateLimited = () => {
    const now = Date.now();
    sendTimestamps.current = sendTimestamps.current.filter(t => now - t < 10000);
    if (sendTimestamps.current.length >= 5) { toast("Slow down — too many messages in a row."); return true; }
    sendTimestamps.current.push(now);
    return false;
  };

  const myDealRooms      = session ? rooms.filter(r => r.type==="deal" && (r.participants||[]).includes(session.user.id)) : [];
  const visibleDealRooms = isAdmin ? rooms.filter(r=>r.type==="deal") : myDealRooms;
  const myDirectRooms    = session ? rooms.filter(r => r.type==="direct" && (r.participants||[]).includes(session.user.id)) : [];
  const activeRoom       = rooms.find(r => r.id === activeRoomId);
  const dealReq          = fetchedDealReq || (activeRoom?.type==="deal" && activeRoom.request_id ? (requests||[]).find(r=>r.id===activeRoom.request_id) : null);
  const dealOffer        = dealReq ? ((dealReq.offers||[]).find(o=>o.status==="accepted") || (dealReq.offers||[]).find(o=>o.status==="open")) : null;

  const openRoom = (id) => { setActiveRoomId(id); setShowChat(true); setShowCounter(false); setCounterInput(""); setUnreadByRoom(prev => ({ ...prev, [id]: 0 })); };

  const sendNegotiationMsg = async (text) => {
    const msgId = crypto.randomUUID();
    const msg = { id:msgId, room_id:activeRoomId, user_id:session.user.id, user_name:session.user.name, text, created_at:new Date().toISOString() };
    await supabase.from("chat_messages").insert(msg);
  };

  const acceptOffer = async () => {
    if (!dealReq) return;
    const price = dealReq.suggestedPrice;
    const updated = { ...dealReq, status:"Accepted", updates:[...(dealReq.updates||[]),{date:new Date().toISOString(),note:`Offer of ₹${price?.toLocaleString("en-IN")} accepted.`}] };
    await upsertOne("requests", updated);
    setFetchedDealReq(updated);
    saveRequests?.((requests||[]).map(r=>r.id===dealReq.id?updated:r));
    await sendNegotiationMsg(`✅ Offer accepted — ₹${price?.toLocaleString("en-IN")}. Let's proceed!`);
  };

  const declineOffer = async () => {
    if (!dealReq) return;
    const updated = { ...dealReq, status:"Completed", lastOfferBy:null, updates:[...(dealReq.updates||[]),{date:new Date().toISOString(),note:"Offer declined."}] };
    await upsertOne("requests", updated);
    setFetchedDealReq(updated);
    saveRequests?.((requests||[]).map(r=>r.id===dealReq.id?updated:r));
    await sendNegotiationMsg("❌ Offer declined.");
  };

  const sendCounter = async () => {
    if (!counterInput) return;
    const newPrice = parseFloat(counterInput);
    if (!newPrice || newPrice <= 0) return;
    const myRole = dealReq?.userId===session.user.id ? "buyer" : "seller";
    const isNewOffer = !dealReq?.suggestedPrice;
    const base = dealReq || {};
    const updated = { ...base, suggestedPrice:newPrice, lastOfferBy:myRole, status:"Match Found", updates:[...(base.updates||[]),{date:new Date().toISOString(),note:`${myRole==="buyer"?"Buyer":"Seller"} ${isNewOffer?"offered":"counter-offered"} ₹${newPrice.toLocaleString("en-IN")}.`}] };
    if (dealReq) {
      await upsertOne("requests", updated);
      saveRequests?.((requests||[]).map(r=>r.id===dealReq.id?updated:r));
    }
    setFetchedDealReq(updated);
    await sendNegotiationMsg(`${isNewOffer?"💰 Offer":"↩ Counter"}: ₹${newPrice.toLocaleString("en-IN")}`);
    setShowCounter(false);
    setCounterInput("");
  };

  return (
    <div className="fade-in" style={{display:"flex",height:"calc(100vh - 210px)",minHeight:520,border:"1px solid var(--bd)",borderRadius:16,overflow:"hidden",background:"#fff"}}>

      {/* ── Sidebar ── */}
      <div className={"chat-sidebar" + (!showChat ? " show" : "")} style={{width:236,borderRight:"1px solid var(--bd)",flexDirection:"column",flexShrink:0,background:"var(--bg2)",display:"flex"}}>
        <div style={{padding:"16px 14px 12px",borderBottom:"1px solid var(--bd)"}}>
          <div style={{fontFamily:"'Playfair Display',serif",fontSize:17,fontWeight:700,marginBottom:10}}>Chat</div>
          <div style={{display:"flex",gap:4}}>
            {(() => {
              const loungeUnread = unreadByRoom["lounge"] || 0;
              const dealUnread = visibleDealRooms?.reduce((s,r)=>s+(unreadByRoom[r.id]||0),0)||0;
              const dmUnread = myDirectRooms?.reduce((s,r)=>s+(unreadByRoom[r.id]||0),0)||0;
              const tabs = [["lounge","Lounge"+(loungeUnread>0?` ${loungeUnread}`:"")]];
              if (session) { tabs.push(["deals","Deals"+(myDealRooms.length?` (${myDealRooms.length})`:"")+(dealUnread>0?` •`:"")]); tabs.push(["direct","DMs"+(dmUnread>0?` •`:"")]); }
              return tabs.map(([t,l])=>(
                <button key={t} onClick={()=>{ setTab(t); if(t==="lounge") openRoom("lounge"); }}
                  style={{flex:1,padding:"5px 0",fontSize:9,fontFamily:"'DM Mono',monospace",letterSpacing:"0.06em",background:tab===t?"#111118":"transparent",color:tab===t?"#fff":(l.includes("•")||(t==="lounge"&&loungeUnread>0))?"var(--accent)":"var(--ink3)",border:tab===t?"none":"1px solid var(--bd)",borderRadius:6,cursor:"pointer"}}>
                  {l.toUpperCase()}
                </button>
              ));
            })()}
          </div>
        </div>

        <div style={{flex:1,overflowY:"auto",padding:"8px 6px"}}>
          {tab==="lounge" && (
            <>
              <button onClick={()=>openRoom("lounge")} style={{width:"100%",textAlign:"left",padding:"10px 10px",borderRadius:10,background:activeRoomId==="lounge"?"#fff":"transparent",border:"none",cursor:"pointer",display:"flex",alignItems:"center",gap:8,marginBottom:2}}>
                <span style={{fontSize:20}}>🌍</span>
                <div style={{flex:1}}>
                  <div style={{fontSize:13,fontWeight:500,color:"var(--ink)"}}>The Lounge</div>
                  <div style={{fontSize:10,color:"var(--ink3)",fontFamily:"'DM Mono',monospace"}}>Everyone welcome</div>
                </div>
                {(unreadByRoom["lounge"]||0)>0 && <span style={{background:"var(--accent)",color:"#fff",borderRadius:10,fontSize:9,fontFamily:"'DM Mono',monospace",padding:"1px 6px",flexShrink:0}}>{unreadByRoom["lounge"]}</span>}
              </button>
              {session && (
                <button onClick={openBot} style={{width:"100%",textAlign:"left",padding:"10px 10px",borderRadius:10,background:activeRoomId==="bot"?"#fff":"transparent",border:"none",cursor:"pointer",display:"flex",alignItems:"center",gap:8,marginBottom:4}}>
                  <span style={{fontSize:20}}>🤖</span>
                  <div style={{flex:1}}>
                    <div style={{fontSize:13,fontWeight:500,color:"var(--ink)"}}>THC Assistant</div>
                    <div style={{fontSize:10,color:"var(--ink3)",fontFamily:"'DM Mono',monospace"}}>Orders &amp; requests help</div>
                  </div>
                </button>
              )}
              {/* Catalog quick search */}
              <div style={{margin:"6px 0 4px",padding:"0 4px"}}>
                <button onClick={()=>setShowCatSearch(p=>!p)} style={{width:"100%",textAlign:"left",padding:"7px 10px",borderRadius:8,background:showCatSearch?"var(--bg3)":"transparent",border:"1px solid var(--bd)",cursor:"pointer",fontSize:10,fontFamily:"'DM Mono',monospace",color:"var(--ink3)",letterSpacing:"0.06em",display:"flex",alignItems:"center",gap:6}}>
                  <span>🔍</span> CATALOG SEARCH
                </button>
                {showCatSearch && (
                  <div style={{marginTop:4}}>
                    <input value={catSearch} onChange={e=>setCatSearch(e.target.value)} placeholder="Brand, model…" style={{fontSize:11,padding:"6px 10px",borderRadius:8,border:"1px solid var(--bd)",width:"100%",boxSizing:"border-box"}}/>
                    <div style={{maxHeight:220,overflowY:"auto",marginTop:4,display:"flex",flexDirection:"column",gap:3}}>
                      {catSearch.trim().length>0 && (() => {
                        const q = catSearch.toLowerCase();
                        const results = watchModels.filter(wm=>`${wm.brand} ${wm.model}`.toLowerCase().includes(q)).slice(0,8);
                        if (!results.length) return <div style={{fontSize:10,color:"var(--ink3)",fontFamily:"'DM Mono',monospace",padding:"8px 4px",textAlign:"center"}}>No matches</div>;
                        return results.map(wm=>{
                          const entries = (catalogEntries||[]).filter(e=>e.watchModelId===wm.id&&e.status==="approved");
                          const allPhotos = [...(wm.photos||[]), ...entries.flatMap(e=>e.photos||[])].map(p=>typeof p==="string"?p:p?.url).filter(Boolean);
                          const photo = allPhotos[0]||null;
                          const wantedCount = entries.filter(e=>e.entryType==="wanted").length;
                          const sharePayload = JSON.stringify({id:wm.id,brand:wm.brand,model:wm.model,decade:wm.decade||"",photo:photo||"",wantedCount,description:wm.description||""});
                          return (
                            <div key={wm.id} style={{display:"flex",gap:7,alignItems:"center",background:"#fff",border:"1px solid var(--bd)",borderRadius:8,padding:"6px 8px",cursor:"default"}}>
                              {photo && <img src={photo} alt="" style={{width:34,height:34,objectFit:"cover",borderRadius:5,flexShrink:0}}/>}
                              <div style={{flex:1,minWidth:0}}>
                                <div style={{fontSize:11,fontWeight:600,color:"var(--ink)",overflow:"hidden",textOverflow:"ellipsis",whiteSpace:"nowrap"}}>{wm.brand} {wm.model}</div>
                                <div style={{fontSize:9,color:"var(--ink3)",fontFamily:"'DM Mono',monospace"}}>{wm.decade||""}{wantedCount>0?" · "+wantedCount+" want this":""}</div>
                              </div>
                              <button onClick={async()=>{
                                const msgId="m"+Date.now()+Math.random().toString(36).slice(2,5);
                                const msg={id:msgId,room_id:activeRoomId,user_id:session.user.id,user_name:session.user.name,text:"WATCH_CARD:"+sharePayload,created_at:new Date().toISOString()};
                                setMessages(prev=>[...prev,msg]);
                                setCatSearch(""); setShowCatSearch(false);
                                await supabase.from("chat_messages").insert(msg);
                              }} style={{fontSize:9,fontFamily:"'DM Mono',monospace",padding:"3px 8px",borderRadius:6,border:"1px solid var(--bd)",background:"var(--bg2)",color:"var(--ink3)",cursor:"pointer",flexShrink:0}}>Share</button>
                            </div>
                          );
                        });
                      })()}
                    </div>
                  </div>
                )}
              </div>
            </>
          )}

          {tab==="deals" && (
            <>
              {isAdmin && <CreateRoomButton rooms={rooms} setRooms={setRooms} requests={requests} users={users} sellers={sellers} session={session}/>}
              {visibleDealRooms.length===0
                ? <div style={{padding:"24px 10px",textAlign:"center",fontFamily:"'DM Mono',monospace",fontSize:10,color:"var(--ink3)",lineHeight:1.7}}>No deal rooms yet.<br/>Created when a seller claims your request.</div>
                : visibleDealRooms.map(r=>(
                    <button key={r.id} onClick={()=>openRoom(r.id)} style={{width:"100%",textAlign:"left",padding:"10px 10px",borderRadius:10,background:activeRoomId===r.id?"#fff":"transparent",border:"none",cursor:"pointer",marginBottom:2,display:"flex",alignItems:"flex-start",gap:4}}>
                      <div style={{flex:1,minWidth:0}}>
                        <div style={{fontSize:13,fontWeight:500,color:"var(--ink)",overflow:"hidden",textOverflow:"ellipsis",whiteSpace:"nowrap",marginBottom:2}}>{r.name}</div>
                        <div style={{display:"flex",gap:4,flexWrap:"wrap"}}>
                          <span className="tag tag-grn" style={{fontSize:8}}>Deal Room</span>
                          {isAdmin && <span className="tag" style={{fontSize:8}}>{(r.participants||[]).length} members</span>}
                        </div>
                      </div>
                      {(unreadByRoom[r.id]||0)>0 && <span style={{background:"var(--accent)",color:"#fff",borderRadius:10,fontSize:9,fontFamily:"'DM Mono',monospace",padding:"1px 6px",flexShrink:0,marginTop:2}}>{unreadByRoom[r.id]}</span>}
                    </button>
                  ))
              }
            </>
          )}

          {tab==="direct" && (() => {
            const dmUserIds = new Set(
              myDirectRooms.flatMap(r => (r.participants||[]).filter(id => id !== session.user.id))
            );
            const messageableUsers = (users || [])
              .filter(usr => usr && usr.id && usr.id !== session.user.id)
              .filter(usr => usr.status !== "suspended" && !usr.suspended)
              .filter(usr => !dmQuery || (usr.name||"").toLowerCase().includes(dmQuery.toLowerCase()) || (usr.email||"").toLowerCase().includes(dmQuery.toLowerCase()))
              .sort((a,b) => {
                const aHas = dmUserIds.has(a.id) ? 0 : 1;
                const bHas = dmUserIds.has(b.id) ? 0 : 1;
                if (aHas !== bHas) return aHas - bHas;
                return (a.name||"").localeCompare(b.name||"");
              });
            const totalActive = (users||[]).filter(u => u && u.id && u.id !== session.user.id && u.status !== "suspended" && !u.suspended).length;
            const sellerLookup = new Map((sellers||[]).map(s => [s.id, s]));
            const roleBadges = (usr) => {
              const r = [...new Set([...(usr.roles||[]), usr.role].filter(Boolean))];
              const isA = r.includes("admin");
              const isS = isA || r.includes("seller");
              const isC = isA || r.includes("collector");
              const isB = isA || r.includes("buyer");
              const dot = (bg, fg, bd, letter) => (
                <span key={letter} style={{width:13,height:13,borderRadius:"50%",background:bg,color:fg,fontSize:7,fontFamily:"'DM Mono',monospace",display:"flex",alignItems:"center",justifyContent:"center",border:`1px solid ${bd}`,fontWeight:700,flexShrink:0}}>{letter}</span>
              );
              return (
                <div style={{display:"flex",gap:2,alignItems:"center"}}>
                  {isA && dot("var(--rbg)","var(--red)","var(--rbd)","A")}
                  {isS && dot("var(--gnbg)","var(--grn)","var(--gnbd)","S")}
                  {isC && dot("var(--gbg)","var(--gold)","var(--gbd)","C")}
                  {isB && !isA && dot("var(--bbg)","var(--blu)","var(--bbd)","B")}
                </div>
              );
            };
            return (
              <>
                {myDirectRooms.length > 0 && (
                  <div style={{padding:"4px 6px 4px",fontFamily:"'DM Mono',monospace",fontSize:9,color:"var(--ink3)",letterSpacing:"0.1em"}}>MY DMS</div>
                )}
                {myDirectRooms.map(r=>(
                  <button key={r.id} onClick={()=>openRoom(r.id)} style={{width:"100%",textAlign:"left",padding:"10px 10px",borderRadius:10,background:activeRoomId===r.id?"#fff":"transparent",border:"none",cursor:"pointer",marginBottom:2,display:"flex",alignItems:"center",gap:8}}>
                    <div className="avatar" style={{width:28,height:28,fontSize:10}}>{r.name?.split(" ").map(w=>w[0]).join("").slice(0,2)}</div>
                    <div style={{fontSize:13,fontWeight:500,color:"var(--ink)",overflow:"hidden",textOverflow:"ellipsis",whiteSpace:"nowrap",flex:1}}>{r.name}</div>
                    {(unreadByRoom[r.id]||0)>0 && <span style={{background:"var(--accent)",color:"#fff",borderRadius:10,fontSize:9,fontFamily:"'DM Mono',monospace",padding:"1px 6px",flexShrink:0}}>{unreadByRoom[r.id]}</span>}
                  </button>
                ))}
                <div style={{padding:"10px 6px 6px",fontFamily:"'DM Mono',monospace",fontSize:9,color:"var(--ink3)",letterSpacing:"0.1em"}}>PEOPLE</div>
                <div style={{padding:"0 6px 8px"}}>
                  <input
                    value={dmQuery}
                    onChange={e=>setDmQuery(e.target.value)}
                    placeholder="Search by name or email…"
                    style={{width:"100%",fontSize:12,padding:"7px 10px",borderRadius:8,border:"1px solid var(--bd)",background:"#fff"}}
                  />
                </div>
                {totalActive === 0 && (
                  <div style={{padding:"16px 8px",fontFamily:"'DM Mono',monospace",fontSize:10,color:"var(--ink3)",textAlign:"center",lineHeight:1.6}}>You're the only user signed up right now.</div>
                )}
                {totalActive > 0 && messageableUsers.length === 0 && (
                  <div style={{padding:"16px 8px",fontFamily:"'DM Mono',monospace",fontSize:10,color:"var(--ink3)",textAlign:"center"}}>No people match.</div>
                )}
                {messageableUsers.map(usr => {
                  const existing = myDirectRooms.find(r => (r.participants||[]).includes(usr.id));
                  const seller = usr.sellerId ? sellerLookup.get(usr.sellerId) : null;
                  const avatarUrl = seller?.avatarUrl || usr.avatar;
                  const initials = (usr.name||"?").split(" ").map(w=>w[0]).join("").slice(0,2).toUpperCase();
                  return (
                    <button key={usr.id} onClick={()=>existing?openRoom(existing.id):startDirect(usr)}
                      style={{width:"100%",textAlign:"left",padding:"8px 10px",borderRadius:10,background:"transparent",border:"none",cursor:"pointer",display:"flex",alignItems:"center",gap:8,marginBottom:2}}>
                      {avatarUrl
                        ? <img src={avatarUrl} className="avatar" style={{width:26,height:26,objectFit:"cover"}} alt=""/>
                        : <div className="avatar" style={{width:26,height:26,fontSize:9}}>{initials}</div>}
                      <div style={{flex:1,minWidth:0}}>
                        <div style={{fontSize:12,color:"var(--ink)",overflow:"hidden",textOverflow:"ellipsis",whiteSpace:"nowrap"}}>{usr.name||usr.email||"Unnamed"}</div>
                        {existing && <div style={{fontFamily:"'DM Mono',monospace",fontSize:8,color:"var(--ink3)",letterSpacing:"0.06em",marginTop:1}}>ALREADY MESSAGING</div>}
                      </div>
                      {roleBadges(usr)}
                    </button>
                  );
                })}
              </>
            );
          })()}
        </div>
      </div>

      {/* ── Messages pane ── */}
      <div className={"chat-pane" + (showChat ? " show" : "")} style={{flex:1,display:"flex",flexDirection:"column",minWidth:0}}>
        {/* Room header */}
        <div style={{padding:"10px 16px",borderBottom:"1px solid var(--bd)",display:"flex",alignItems:"center",gap:10,background:"#fafafa"}}>
          <button className="chat-back" onClick={()=>setShowChat(false)} style={{display:"none",background:"none",border:"none",fontSize:18,cursor:"pointer",color:"var(--ink2)",padding:"0 4px 0 0",flexShrink:0}}>←</button>
          <div style={{flex:1,minWidth:0}}>
            <div style={{fontWeight:600,fontSize:14,overflow:"hidden",textOverflow:"ellipsis",whiteSpace:"nowrap"}}>{activeRoomId==="bot"?"THC Assistant":activeRoom?.name || "The Lounge"}</div>
            <div style={{fontSize:11,color:"var(--ink3)",fontFamily:"'DM Mono',monospace",marginTop:1}}>
              {activeRoomId==="bot"?"Your data only · no external knowledge":activeRoom?.type==="deal"?"Deal in progress · Buyer + Seller":activeRoom?.type==="direct"?"Direct Message":"Open to all members"}
            </div>
          </div>
          {isAdmin && activeRoom?.type!=="deal" && (
            <button onClick={()=>{if(window.confirm("Clear all messages in this room?")) clearMessages();}}
              style={{background:"none",border:"1px solid var(--rbd)",color:"var(--red)",fontSize:10,fontFamily:"'DM Mono',monospace",padding:"4px 10px",borderRadius:20,cursor:"pointer",whiteSpace:"nowrap",flexShrink:0}}>
              Clear
            </button>
          )}
          {isAdmin && activeRoom?.type!=="deal" && activeRoomId !== "lounge" && (
            <button onClick={deleteRoom}
              style={{background:"var(--red)",border:"none",color:"#fff",fontSize:10,fontFamily:"'DM Mono',monospace",padding:"4px 10px",borderRadius:20,cursor:"pointer",whiteSpace:"nowrap",flexShrink:0}}>
              Delete
            </button>
          )}
        </div>

        {/* Deal context card — collapsed by default, tap to expand */}
        {dealReq && (() => {
          const STEPS = ["Offer","Accepted","Dispatched","Delivered","Done"];
          const statusMap = {"Match Found":"Accepted","Dispatched":"Dispatched","Delivered":"Delivered","Completed":"Done"};
          const cur = statusMap[dealReq.status] || (dealOffer?.status==="accepted"?"Accepted":"Offer");
          const curIdx = STEPS.indexOf(cur);
          return (
            <div style={{borderBottom:"1px solid var(--bd)",background:"var(--bg2)"}}>
              {/* Toggle strip — one clean row */}
              <button onClick={()=>setDealCtxOpen(o=>!o)} style={{width:"100%",display:"flex",alignItems:"center",gap:10,padding:"9px 14px",background:"none",border:"none",cursor:"pointer",textAlign:"left"}}>
                <svg width="12" height="12" viewBox="0 0 12 12" fill="none" stroke="var(--ink3)" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" style={{flexShrink:0,transition:"transform .2s",transform:dealCtxOpen?"rotate(180deg)":"rotate(0deg)"}}>
                  <polyline points="2,4 6,8 10,4"/>
                </svg>
                <span style={{fontSize:13,fontWeight:500,color:"var(--ink)",overflow:"hidden",textOverflow:"ellipsis",whiteSpace:"nowrap",flex:1}}>{dealReq.watch}</span>
                <span style={{fontFamily:"'DM Mono',monospace",fontSize:9,padding:"3px 8px",borderRadius:20,background:cur==="Done"?"#ECFDF5":cur==="Accepted"?"#FFFBEB":"var(--bg3)",color:cur==="Done"?"var(--grn)":cur==="Accepted"?"var(--gold)":"var(--ink2)",border:"1px solid",borderColor:cur==="Done"?"var(--gnbd)":cur==="Accepted"?"#FDE68A":"var(--bd)",flexShrink:0,letterSpacing:"0.06em"}}>{cur}</span>
              </button>
              {/* Expanded body */}
              {dealCtxOpen && (
                <div style={{padding:"0 14px 14px"}}>
                  {/* Progress track */}
                  <div style={{display:"flex",alignItems:"center",gap:0,marginBottom:14}}>
                    {STEPS.map((s,i)=>(
                      <React.Fragment key={s}>
                        <div style={{display:"flex",flexDirection:"column",alignItems:"center",gap:3,flex:i<STEPS.length-1?0:0,flexShrink:0}}>
                          <span style={{width:10,height:10,borderRadius:"50%",background:i<=curIdx?"var(--accent)":"var(--bd)",display:"block",border:i===curIdx?"2px solid var(--accent)":"none",boxSizing:"border-box",outline:i===curIdx?"2px solid rgba(5,150,105,.2)":"none"}}/>
                          <span style={{fontFamily:"'DM Mono',monospace",fontSize:7.5,color:i<=curIdx?"var(--accent)":"var(--ink3)",whiteSpace:"nowrap",letterSpacing:"0.04em"}}>{s}</span>
                        </div>
                        {i<STEPS.length-1&&<div style={{flex:1,height:2,background:i<curIdx?"var(--accent)":"var(--bd)",marginBottom:14,minWidth:8}}/>}
                      </React.Fragment>
                    ))}
                  </div>
                  {/* Deal summary — adapts to deal type */}
                  {dealReq.type==="enquiry" ? (
                    /* Suggest-a-price deal: show listed price vs offered price */
                    <div style={{display:"grid",gridTemplateColumns:"1fr 1fr",gap:8,marginBottom:10}}>
                      {dealReq.listedPrice > 0 && (
                        <div style={{background:"#fff",borderRadius:8,padding:"8px 10px"}}>
                          <div style={{fontFamily:"'DM Mono',monospace",fontSize:8,color:"var(--ink3)",letterSpacing:"0.1em",marginBottom:4}}>LISTED AT</div>
                          <div style={{fontSize:14,fontWeight:700,color:"var(--ink)"}}>₹{Number(dealReq.listedPrice).toLocaleString("en-IN")}</div>
                        </div>
                      )}
                      {dealReq.suggestedPrice > 0 && (
                        <div style={{background: dealReq.status==="Accepted"?"#ECFDF5":"#FFFBEB",borderRadius:8,padding:"8px 10px"}}>
                          <div style={{fontFamily:"'DM Mono',monospace",fontSize:8,letterSpacing:"0.1em",marginBottom:4,color:dealReq.status==="Accepted"?"var(--grn)":"var(--gold)"}}>
                            {dealReq.status==="Accepted"?"AGREED PRICE":"BUYER'S OFFER"}
                          </div>
                          <div style={{fontSize:14,fontWeight:700,color:"var(--ink)"}}>₹{Number(dealReq.suggestedPrice).toLocaleString("en-IN")}</div>
                          {dealReq.listedPrice > 0 && dealReq.status!=="Accepted" && (
                            <div style={{fontSize:9,color:"var(--ink3)",fontFamily:"'DM Mono',monospace",marginTop:2}}>
                              {Math.round((1-(dealReq.suggestedPrice/dealReq.listedPrice))*100)}% below ask
                            </div>
                          )}
                        </div>
                      )}
                    </div>
                  ) : (
                    /* Sourcing deal: show budget/condition/offer */
                    <div style={{display:"grid",gridTemplateColumns:"1fr 1fr",gap:8,marginBottom:dealOffer?10:0}}>
                      <div style={{background:"#fff",borderRadius:8,padding:"8px 10px"}}>
                        <div style={{fontFamily:"'DM Mono',monospace",fontSize:8,color:"var(--ink3)",letterSpacing:"0.1em",marginBottom:4}}>REQUEST</div>
                        {dealReq.budget && <div style={{fontSize:12,fontWeight:600,color:"var(--ink)"}}>₹{Number(dealReq.budget).toLocaleString("en-IN")} budget</div>}
                        {dealReq.condition && <div style={{fontSize:11,color:"var(--ink3)",marginTop:2}}>{dealReq.condition}</div>}
                        {dealReq.decade && <div style={{fontSize:10,color:"var(--ink3)",fontFamily:"'DM Mono',monospace",marginTop:1}}>{dealReq.decade}</div>}
                      </div>
                      {dealOffer && (
                        <div style={{background:"#fff",borderRadius:8,padding:"8px 10px"}}>
                          <div style={{fontFamily:"'DM Mono',monospace",fontSize:8,color:"var(--ink3)",letterSpacing:"0.1em",marginBottom:4}}>OFFER · <span style={{color:dealOffer.status==="accepted"?"var(--grn)":"var(--gold)"}}>{dealOffer.status.toUpperCase()}</span></div>
                          <div style={{fontSize:14,fontWeight:700,color:"var(--ink)"}}>₹{Number(dealOffer.price).toLocaleString("en-IN")}</div>
                          {dealOffer.condition && <div style={{fontSize:11,color:"var(--ink3)",marginTop:2}}>{dealOffer.condition}</div>}
                        </div>
                      )}
                    </div>
                  )}
                  {/* Buyer name always shown */}
                  {dealReq.name && <div style={{fontFamily:"'DM Mono',monospace",fontSize:9,color:"var(--ink3)",marginBottom:6,letterSpacing:"0.06em"}}>BUYER · {dealReq.name}</div>}
                  {/* Notes */}
                  {dealReq.notes && <div style={{fontSize:11,color:"var(--ink2)",lineHeight:1.5,marginBottom:6,background:"#fff",borderRadius:8,padding:"6px 10px"}}>{dealReq.notes}</div>}
                  {dealOffer?.note && <div style={{fontSize:11,color:"var(--ink2)",lineHeight:1.5,marginBottom:6,background:"#fff",borderRadius:8,padding:"6px 10px"}}>{dealOffer.note}</div>}
                  {dealReq.trackingNumber && (
                    <div style={{background:"rgba(5,150,105,.08)",borderRadius:8,padding:"7px 10px",marginBottom:6,fontFamily:"'DM Mono',monospace",fontSize:10,color:"var(--grn)"}}>
                      📦 {dealReq.courier||"Courier"} · {dealReq.trackingNumber}
                    </div>
                  )}
                  {isAdmin && (
                    <div style={{fontFamily:"'DM Mono',monospace",fontSize:9,color:"var(--accent)",marginBottom:6}}>
                      {dealReq.name||"?"} (buyer) · {dealReq.sellerClaim?.sellerName||dealOffer?.sellerName||"?"} (seller)
                    </div>
                  )}
                  {/* Photos — horizontal scroll */}
                  {((dealReq.photos||[]).length > 0 || (dealOffer?.photos||[]).length > 0) && (
                    <div style={{display:"flex",gap:6,overflowX:"auto",scrollbarWidth:"none",paddingBottom:2}}>
                      {[...(dealReq.photos||[]), ...(dealOffer?.photos||[])].slice(0,8).map((p,i)=>(
                        <a key={i} href={p} target="_blank" rel="noopener noreferrer" style={{flexShrink:0}}>
                          <img src={p} alt="" style={{width:56,height:56,objectFit:"cover",borderRadius:8,border:"1px solid var(--bd)",display:"block"}}/>
                        </a>
                      ))}
                    </div>
                  )}
                  {/* Admin actions */}
                  {isAdmin && (
                    <div style={{display:"flex",gap:6,marginTop:10}}>
                      <button onClick={()=>{if(window.confirm("Clear all messages?")) clearMessages();}}
                        style={{background:"none",border:"1px solid var(--rbd)",color:"var(--red)",fontSize:10,fontFamily:"'DM Mono',monospace",padding:"4px 10px",borderRadius:20,cursor:"pointer"}}>
                        Clear messages
                      </button>
                      {activeRoomId !== "lounge" && (
                        <button onClick={deleteRoom}
                          style={{background:"var(--red)",border:"none",color:"#fff",fontSize:10,fontFamily:"'DM Mono',monospace",padding:"4px 10px",borderRadius:20,cursor:"pointer"}}>
                          Delete room
                        </button>
                      )}
                    </div>
                  )}
                </div>
              )}
            </div>
          );
        })()}

        {/* ── Bot pane ── */}
        {activeRoomId==="bot" && (
          <>
            <div style={{flex:1,overflowY:"auto",padding:"20px 20px 8px",display:"flex",flexDirection:"column",gap:10}}>
              {botHistory.map((msg, i) => (
                <div key={i} style={{display:"flex",flexDirection:"column",alignItems:msg.role==="user"?"flex-end":"flex-start",gap:6}}>
                  <div style={{maxWidth:"82%",padding:"10px 14px",borderRadius:msg.role==="user"?"18px 18px 4px 18px":"18px 18px 18px 4px",background:msg.role==="user"?"#111118":"#F4F4F5",color:msg.role==="user"?"#fff":"var(--ink)",fontSize:13,lineHeight:1.6,whiteSpace:"pre-wrap"}}>
                    {msg.text}
                  </div>
                  {msg.chips?.length > 0 && (
                    <div style={{display:"flex",flexWrap:"wrap",gap:6,maxWidth:"90%"}}>
                      {msg.chips.map(c=>(
                        <button key={c} onClick={()=>botRespond(c)} style={{background:"#fff",border:"1.5px solid var(--bd)",borderRadius:20,padding:"6px 14px",fontSize:12,fontFamily:"'DM Mono',monospace",color:"var(--ink2)",cursor:"pointer",letterSpacing:"0.04em"}}>
                          {c}
                        </button>
                      ))}
                    </div>
                  )}
                </div>
              ))}
              <div ref={bottomRef}/>
            </div>
            <div style={{padding:"10px 14px",borderTop:"1px solid var(--bd)",background:"#fafafa",fontFamily:"'DM Mono',monospace",fontSize:10,color:"var(--ink3)",textAlign:"center",letterSpacing:"0.06em"}}>
              ASSISTANT · DATA FROM YOUR ACCOUNT ONLY
            </div>
          </>
        )}

        {/* Messages */}
        {activeRoomId!=="bot" && <div style={{flex:1,overflowY:"auto",padding:"20px 20px 8px",display:"flex",flexDirection:"column",gap:1}}>
          {loading && <div style={{textAlign:"center",padding:32,fontFamily:"'DM Mono',monospace",fontSize:11,color:"var(--ink3)"}}>Loading…</div>}
          {!loading && messages.length===0 && (
            <div style={{textAlign:"center",padding:"60px 0",fontFamily:"'DM Mono',monospace",fontSize:11,color:"var(--ink3)",lineHeight:1.8}}>
              {activeRoom?.type==="lounge"||!activeRoom ? "Welcome to the Lounge. Talk watches." : "No messages yet. Start the conversation."}<br/>
              <span style={{fontSize:20,marginTop:8,display:"block"}}>👋</span>
            </div>
          )}
          {(() => {
            const isDealRoom = activeRoom?.type === "deal";
            const buyerUserId = dealReq?.userId;
            const getDisplayName = (msg) => {
              if (msg.user_id === session.user.id) return "You";
              if (!isDealRoom) return msg.user_name;
              if (isAdmin) {
                const role = msg.user_id === buyerUserId ? "Buyer" : "Seller";
                return `${msg.user_name} (${role})`;
              }
              return msg.user_id === buyerUserId ? "Buyer" : "Seller";
            };
            const getInitials = (msg) => {
              if (!isDealRoom) return (msg.user_name||"?").split(" ").map(w=>w[0]).join("").slice(0,2).toUpperCase();
              if (isAdmin) return (msg.user_name||"?").split(" ").map(w=>w[0]).join("").slice(0,2).toUpperCase();
              return msg.user_id === buyerUserId ? "B" : "S";
            };
            return messages.map((m, i) => {
            const isMe = m.user_id === session.user.id;
            const prevSame = i>0 && messages[i-1].user_id===m.user_id;
            const initials = getInitials(m);
            const displayName = getDisplayName(m);
            const timeStr = new Date(m.created_at).toLocaleTimeString("en-IN",{hour:"2-digit",minute:"2-digit"});
            return (
              <div key={m.id} style={{display:"flex",gap:8,alignItems:"flex-end",flexDirection:isMe?"row-reverse":"row",marginTop:prevSame?2:10}}>
                {!prevSame
                  ? <div className="avatar" style={{width:26,height:26,fontSize:9,flexShrink:0,marginBottom:0}}>{initials}</div>
                  : <div style={{width:26,flexShrink:0}}/>
                }
                <div style={{maxWidth:"65%"}}>
                  {!prevSame && <div style={{fontSize:10,color:"var(--ink3)",marginBottom:3,textAlign:isMe?"right":"left",fontFamily:"'DM Mono',monospace"}}>
                    {displayName} · {timeStr}
                  </div>}
                  {m.image_url ? (
                    <a href={m.image_url} target="_blank" rel="noopener noreferrer">
                      <img src={m.image_url} alt="photo" style={{maxWidth:220,maxHeight:260,borderRadius:12,display:"block",objectFit:"cover"}}/>
                    </a>
                  ) : m.text?.startsWith("WATCH_CARD:") ? (() => {
                    try {
                      const card = JSON.parse(m.text.slice("WATCH_CARD:".length));
                      return (
                        <div style={{background:"#fff",border:"1.5px solid var(--bd)",borderRadius:14,overflow:"hidden",maxWidth:240,boxShadow:"0 2px 8px rgba(0,0,0,.06)"}}>
                          {card.photo && <img src={card.photo} alt="" style={{width:"100%",height:120,objectFit:"cover",display:"block"}}/>}
                          <div style={{padding:"10px 12px"}}>
                            <div style={{fontFamily:"'Playfair Display',serif",fontSize:15,fontWeight:600,color:"var(--ink)",marginBottom:3}}>{card.brand} {card.model}</div>
                            {card.decade && <div style={{fontSize:10,color:"var(--ink3)",fontFamily:"'DM Mono',monospace",marginBottom:4}}>{card.decade}</div>}
                            {card.wantedCount>0 && <div style={{fontSize:10,color:"var(--accent)",marginBottom:6}}>{card.wantedCount} member{card.wantedCount!==1?"s":""} want this</div>}
                            {card.description && <div style={{fontSize:11,color:"var(--ink2)",lineHeight:1.5,marginBottom:8}}>{card.description.slice(0,90)}{card.description.length>90?"…":""}</div>}
                            <button onClick={()=>setView&&setView("catalog")} style={{fontSize:11,fontFamily:"'DM Mono',monospace",padding:"5px 10px",borderRadius:20,border:"1px solid var(--bd)",background:"var(--bg2)",color:"var(--ink2)",cursor:"pointer",width:"100%"}}>View in Catalog →</button>
                          </div>
                        </div>
                      );
                    } catch { return <div style={{background:"var(--bg2)",padding:"9px 13px",borderRadius:14,fontSize:13.5}}>{m.text}</div>; }
                  })() : (
                  <div style={{background:isMe?"#111118":"var(--bg2)",color:isMe?"#fff":"var(--ink)",padding:"9px 13px",borderRadius:isMe?"14px 4px 14px 14px":"4px 14px 14px 14px",fontSize:13.5,lineHeight:1.55,wordBreak:"break-word"}}>
                    {m.text}
                  </div>
                  )}
                </div>
              </div>
            );
          });
          })()}
          <div ref={bottomRef}/>
        </div>}

        {/* Quick suggestions for deal rooms */}
        {activeRoomId!=="bot" && session && canSend && activeRoom?.type==="deal" && (() => {
          const isBuyer = dealReq?.userId === session.user.id;
          const status = dealReq?.status || "";
          const offerStatus = dealOffer?.status || "";
          let chips = [];
          if (offerStatus==="open" && isBuyer) chips = ["Can you do a better price?","What's the condition like?","Can I see more photos?","Deal confirmed ✓","I'll take it at this price"];
          else if (offerStatus==="open" && !isBuyer) chips = ["Watch is in excellent condition","Happy to send more photos","Price is firm","I can do a small discount","Ready to ship within 2 days"];
          else if (offerStatus==="accepted" && isBuyer) chips = ["Please share tracking details","When will you dispatch?","Address confirmed ✓"];
          else if (offerStatus==="accepted" && !isBuyer) chips = ["I'll dispatch by tomorrow","Tracking number coming shortly","Packed and ready to go"];
          else if (status==="Dispatched") chips = isBuyer ? ["Watch received ✓","Tracking shows out for delivery","Can you share more tracking info?"] : ["Let me know when it arrives","Reach out if any issues"];
          else chips = ["Sounds good","Can we discuss?","I'll get back to you shortly"];
          if (!chips.length) return null;
          return (
            <div className="mob-hide" style={{display:"flex",gap:6,padding:"4px 14px 0",overflowX:"auto",scrollbarWidth:"none",flexShrink:0}}>
              {chips.map(c=>(
                <button key={c} onClick={()=>setDraft(c)} style={{whiteSpace:"nowrap",background:"var(--bg2)",border:"1px solid var(--bd)",borderRadius:20,padding:"4px 11px",fontSize:11,fontFamily:"'DM Mono',monospace",color:"var(--ink2)",cursor:"pointer",flexShrink:0}}>
                  {c}
                </button>
              ))}
            </div>
          );
        })()}

        {/* Negotiation action bar — all deal rooms */}
        {activeRoomId!=="bot" && session && activeRoom?.type==="deal" && !["Dispatched","Delivered"].includes(dealReq?.status) && (() => {
          const myUserId = session.user.id;
          const mySellerId = session.user.sellerId;
          // Am I the buyer for this specific deal?
          const isBuyer = dealReq?.userId === myUserId;
          // Am I the seller for this specific deal? Check sellerClaim, sellerId field, or room participants minus the buyer
          const dealSellerUserId = (() => {
            const u = users?.find(u => u.sellerId === (dealReq?.sellerClaim?.sellerId || dealReq?.sellerId));
            return u?.id;
          })();
          const isSeller = !isBuyer && (
            mySellerId === (dealReq?.sellerClaim?.sellerId || dealReq?.sellerId) ||
            myUserId === dealSellerUserId ||
            // fallback: I'm in room participants and I'm a seller role but not the buyer
            (((session.user.roles||[]).includes("seller") || session.user.role==="seller" || session.user.role==="admin") &&
             (activeRoom?.participants||[]).includes(myUserId))
          );

          // Current price — from suggestedPrice OR latest open offer in offers[]
          const latestOffer = (dealReq?.offers||[]).filter(o=>o.status==="open").slice(-1)[0];
          const acceptedOffer = (dealReq?.offers||[]).find(o=>o.status==="accepted");
          const price = dealReq?.suggestedPrice || latestOffer?.price || 0;
          const lastOfferBy = dealReq?.lastOfferBy || (latestOffer ? "seller" : null);
          // Has this seller already made an open offer via offers[]?
          const myExistingOffer = mySellerId ? (dealReq?.offers||[]).find(o=>o.sellerId===mySellerId && o.status==="open") : null;
          const dealClosed = ["Completed","Accepted"].includes(dealReq?.status) || !!acceptedOffer;

          // No price on table yet — show "Send offer" for seller (unless they already have one)
          if (!price && !showCounter) {
            if (!isSeller || myExistingOffer) return null;
            return (
              <div style={{padding:"10px 14px",borderTop:"1px solid var(--bd)",background:"var(--bg2)",display:"flex",alignItems:"center",gap:8}}>
                <span style={{fontFamily:"'DM Mono',monospace",fontSize:10,color:"var(--ink3)",flex:1,letterSpacing:"0.06em"}}>SEND PRICE OFFER</span>
                <button className="btn-ink" style={{fontSize:12,padding:"7px 16px",borderRadius:10}} onClick={()=>setShowCounter(true)}>₹ Make offer</button>
              </div>
            );
          }

          // Price input (counter or new offer)
          if (showCounter) return (
            <div style={{padding:"10px 14px",borderTop:"2px solid var(--accent)",background:"rgba(5,150,105,.04)"}}>
              <div style={{fontFamily:"'DM Mono',monospace",fontSize:9,color:"var(--grn)",letterSpacing:"0.12em",marginBottom:8}}>
                {price ? "COUNTER-OFFER" : "YOUR OFFER"}
                {price > 0 && <span style={{color:"var(--ink3)",marginLeft:8}}>current: ₹{price.toLocaleString("en-IN")}</span>}
              </div>
              <div style={{display:"flex",gap:8,alignItems:"center"}}>
                <input type="number" inputMode="numeric" value={counterInput}
                  onChange={e=>setCounterInput(e.target.value)}
                  onKeyDown={e=>{if(e.key==="Enter")sendCounter();}}
                  placeholder="₹ amount"
                  style={{flex:1,fontSize:15,padding:"8px 12px",borderRadius:10,border:"1.5px solid var(--bd)",fontFamily:"'Playfair Display',serif"}}
                  autoFocus
                />
                <button className="btn-ink" style={{fontSize:12,padding:"9px 16px",borderRadius:10,flexShrink:0}} onClick={sendCounter}>Send →</button>
                <button className="btn-ghost" style={{fontSize:12,padding:"9px 12px",borderRadius:10,flexShrink:0}} onClick={()=>{setShowCounter(false);setCounterInput("");}}>✕</button>
              </div>
            </div>
          );

          // Deal closed
          if (dealClosed) return (
            <div style={{padding:"10px 16px",borderTop:"1px solid var(--bd)",background:"#ECFDF5",display:"flex",alignItems:"center",gap:8}}>
              <span style={{fontSize:16}}>✅</span>
              <span style={{fontFamily:"'DM Mono',monospace",fontSize:10,color:"var(--grn)",letterSpacing:"0.06em"}}>
                {dealReq.status==="Accepted" ? `DEAL ACCEPTED · ₹${price.toLocaleString("en-IN")}` : "DEAL CLOSED"}
              </span>
            </div>
          );

          // Price on table — whose turn?
          const myTurn = lastOfferBy ? ((lastOfferBy==="buyer" && !isBuyer) || (lastOfferBy==="seller" && isBuyer)) : isSeller;
          if (!myTurn) return (
            <div style={{padding:"10px 16px",borderTop:"1px solid var(--bd)",background:"#FFFBEB",display:"flex",alignItems:"center",justifyContent:"space-between"}}>
              <span style={{fontFamily:"'DM Mono',monospace",fontSize:10,color:"var(--gold)",letterSpacing:"0.06em"}}>
                {lastOfferBy==="buyer"?"BUYER'S":"SELLER'S"} OFFER · ₹{price.toLocaleString("en-IN")} · Waiting for response…
              </span>
            </div>
          );
          return (
            <div style={{padding:"10px 14px",borderTop:"2px solid var(--accent)",background:"rgba(5,150,105,.04)"}}>
              <div style={{display:"flex",alignItems:"center",justifyContent:"space-between",marginBottom:8}}>
                <span style={{fontFamily:"'DM Mono',monospace",fontSize:9,color:"var(--grn)",letterSpacing:"0.12em"}}>
                  {lastOfferBy==="buyer"?"BUYER'S OFFER":"SELLER'S OFFER"}
                </span>
                <span style={{fontFamily:"'Playfair Display',serif",fontSize:20,fontWeight:700,color:"var(--ink)"}}>₹{price.toLocaleString("en-IN")}</span>
              </div>
              <div style={{display:"flex",gap:8}}>
                <button style={{flex:2,background:"var(--grn)",color:"#fff",border:"none",borderRadius:10,padding:"10px 0",fontSize:13,fontWeight:700,cursor:"pointer"}} onClick={acceptOffer}>✓ Accept ₹{price.toLocaleString("en-IN")}</button>
                <button style={{flex:1,background:"#fff",border:"1.5px solid var(--bd)",borderRadius:10,padding:"10px 0",fontSize:12,cursor:"pointer",color:"var(--ink2)"}} onClick={()=>{setShowCounter(true);setCounterInput("");}}>↩ Counter</button>
                <button style={{flex:1,background:"none",border:"1.5px solid rgba(225,29,72,.3)",color:"var(--red)",borderRadius:10,padding:"10px 0",fontSize:12,cursor:"pointer"}} onClick={declineOffer}>✗ Decline</button>
              </div>
            </div>
          );
        })()}

        {/* Input bar — hidden in bot mode */}
        {activeRoomId!=="bot" && (!session ? (
          <div style={{padding:"14px 18px",borderTop:"1px solid var(--bd)",background:"var(--bg2)",display:"flex",alignItems:"center",justifyContent:"space-between",gap:12}}>
            <div style={{fontSize:13,color:"var(--ink2)",lineHeight:1.4}}>
              <strong>Join the conversation</strong>
              <div style={{fontSize:11,color:"var(--ink3)",fontFamily:"'DM Mono',monospace",marginTop:2}}>Sign in to send messages</div>
            </div>
            <button className="btn-ink" style={{fontSize:12,padding:"8px 18px",flexShrink:0}} onClick={openAuth}>Sign in →</button>
          </div>
        ) : !canSend ? (
          <div style={{padding:"12px 16px",borderTop:"1px solid var(--bd)",background:"var(--bg2)",display:"flex",alignItems:"center",gap:10}}>
            <span style={{fontSize:16}}>🔒</span>
            <div style={{fontSize:12,color:"var(--ink3)",fontFamily:"'DM Mono',monospace",lineHeight:1.5}}>
              Chat unlocks after your first request.{" "}
              <button onClick={()=>setView?.("request")} style={{background:"none",border:"none",color:"var(--accent)",cursor:"pointer",fontFamily:"inherit",fontSize:"inherit",padding:0,textDecoration:"underline"}}>Submit a find request →</button>
            </div>
          </div>
        ) : (
        <div style={{padding:"10px 14px",borderTop:"1px solid var(--bd)",display:"flex",gap:8,alignItems:"flex-end",background:"#fafafa"}}>
          <input ref={photoInputRef} type="file" accept="image/*" style={{display:"none"}} onChange={e=>{const f=e.target.files[0];if(f)sendPhoto(f);e.target.value="";}}/>
          <button onClick={()=>photoInputRef.current?.click()} disabled={uploading}
            style={{background:"none",border:"1.5px solid var(--bd)",borderRadius:50,padding:"9px 12px",cursor:"pointer",fontSize:15,flexShrink:0,opacity:uploading?0.4:1}}
            title="Send photo">📷</button>
          <textarea value={draft} onChange={e=>setDraft(e.target.value)}
            onKeyDown={e=>{if(e.key==="Enter"&&!e.shiftKey){e.preventDefault();sendMessage();}}}
            placeholder="Message… (Enter to send, Shift+Enter for new line)"
            rows={1} style={{flex:1,resize:"none",padding:"10px 14px",borderRadius:50,border:"1.5px solid var(--bd)",fontFamily:"'DM Sans',sans-serif",fontSize:13.5,outline:"none",lineHeight:1.5,maxHeight:90,overflowY:"auto"}}
          />
          <button onClick={sendMessage} disabled={!draft.trim()} className="btn-ink" style={{padding:"10px 18px",flexShrink:0,opacity:draft.trim()?1:0.35,borderRadius:50}}>→</button>
        </div>
        ) : null)}
      </div>
    </div>
  );
}

// ─── Admin: Create Deal Room ──────────────────────────────────────────────────

// ─── Create Room Button ───────────────────────────────────────────────────────
export function CreateRoomButton({ rooms, setRooms, requests, users, sellers, session }) {
  const [open, setOpen] = useState(false);
  const [name, setName] = useState("");
  const [selBuyer, setSelBuyer]   = useState("");
  const [selSeller, setSelSeller] = useState("");
  const [saving, setSaving] = useState(false);

  const create = async () => {
    if (!name.trim()) return;
    setSaving(true);
    const participants = [session.user.id, selBuyer, selSeller].filter(Boolean);
    const id = "deal-custom-" + Date.now();
    const { error } = await supabase.from("chat_rooms").insert({ id, type:"deal", name:name.trim(), participants, created_at:new Date().toISOString() });
    if (error) { alert("Failed to create room: " + error.message); setSaving(false); return; }
    const { data } = await supabase.from("chat_rooms").select("*");
    setRooms(data || []);
    setName(""); setSelBuyer(""); setSelSeller(""); setSaving(false); setOpen(false);
  };

  return (
    <div style={{padding:"8px 6px 4px"}}>
      {!open
        ? <button onClick={()=>setOpen(true)} style={{width:"100%",padding:"8px 10px",borderRadius:8,background:"var(--gnbg)",border:"1px solid var(--gnbd)",color:"var(--grn)",fontSize:11,fontFamily:"'DM Mono',monospace",cursor:"pointer",letterSpacing:"0.06em"}}>+ CREATE ROOM</button>
        : <div style={{background:"#fff",border:"1px solid var(--bd)",borderRadius:10,padding:10,marginBottom:4}}>
            <div style={{fontFamily:"'DM Mono',monospace",fontSize:9,color:"var(--ink3)",marginBottom:6,letterSpacing:"0.1em"}}>NEW DEAL ROOM</div>
            <input value={name} onChange={e=>setName(e.target.value)} placeholder="Room name" style={{marginBottom:6,fontSize:12,padding:"7px 10px"}}/>
            <select value={selBuyer} onChange={e=>setSelBuyer(e.target.value)} style={{marginBottom:6,fontSize:12,padding:"7px 10px"}}>
              <option value="">Add buyer…</option>
              {users.filter(u=>!((u.roles||[u.role]).includes("seller"))).map(u=><option key={u.id} value={u.id}>{u.name}</option>)}
            </select>
            <select value={selSeller} onChange={e=>setSelSeller(e.target.value)} style={{marginBottom:8,fontSize:12,padding:"7px 10px"}}>
              <option value="">Add seller…</option>
              {sellers.map(s=>{const su=users.find(u=>u.sellerId===s.id||u.id===s.userId);return su?<option key={s.id} value={su.id}>{s.name}</option>:null;})}
            </select>
            <div style={{display:"flex",gap:6}}>
              <button onClick={create} disabled={saving||!name.trim()} className="btn-ink" style={{flex:1,padding:"7px 0",fontSize:11}}>Create</button>
              <button onClick={()=>setOpen(false)} className="btn-ghost" style={{flex:1,padding:"7px 0",fontSize:11}}>Cancel</button>
            </div>
          </div>
      }
    </div>
  );
}
